import dayjs, { type Dayjs } from "dayjs"

import { type DataWithId } from "#customTypes/types"

import { getNow } from "./dateHelpers"
import { SEMESTER_DATES } from "./hardcodedSettings"

export const SEMESTER_CALENDAR_COLLECTION = "semesters"
export const SEMESTER_CALENDAR_DATE_FORMAT = "YYYY/MM/DD"

export type SemesterStart = {
  id: string
  startDate: string
}

export type SemesterCalendar = {
  semesters: SemesterStart[]
}

export type SemesterCalendarDoc = DataWithId & {
  startDate?: string | null
}

export const nextSemesterId = (id: string): string => {
  const [yearText, letter] = id.split(" ")
  const year = Number(yearText)
  if (!yearText || !letter || !Number.isFinite(year)) {
    throw new Error(`Semester id "${id}" is not YYYY A|B|C`)
  }
  if (letter === "A") return `${yearText} B`
  if (letter === "B") return `${yearText} C`
  if (letter === "C") return `${year + 1} A`
  throw new Error(`Semester id "${id}" is not YYYY A|B|C`)
}

export const seedSemesterCalendar = (): SemesterCalendar => ({
  semesters: SEMESTER_DATES.map((row) => ({
    id: row[0],
    startDate: row[1],
  })),
})

// Hardcoded starts until a listener installs a complete Firestore calendar.
// A snapshot missing any seed semester, an empty snapshot, and a listener
// failure all leave these starts in place. Helpers read this directly.
let activeCalendar = seedSemesterCalendar()

export const getActiveSemesterCalendar = (): SemesterCalendar => activeCalendar

export const installSemesterCalendar = (
  calendar: SemesterCalendar | null
): void => {
  activeCalendar =
    calendar && calendar.semesters.length > 0
      ? calendar
      : seedSemesterCalendar()
}

// A short `semesters` collection already exists for the current terms. It is
// not a calendar unless it begins at the first seed semester and has no hole.
// A missing tail is a calendar that was trimmed from the end. Later terms are
// kept only while they continue that ladder without a gap.
export const calendarFromDocs = (
  docs: SemesterCalendarDoc[]
): SemesterCalendar | null => {
  const starts = new Map<string, string>()
  for (const row of docs) {
    if (row.id.split(" ").length !== 2 || !row.startDate) continue
    starts.set(row.id, row.startDate)
  }
  if (starts.size === 0) return null

  const seed = seedSemesterCalendar().semesters
  let prefix = 0
  for (; prefix < seed.length; prefix += 1) {
    if (!starts.has(seed[prefix].id)) break
  }
  if (prefix === 0) return null
  for (let index = prefix; index < seed.length; index += 1) {
    if (starts.has(seed[index].id)) return null
  }

  const semesters: SemesterStart[] = []
  for (let index = 0; index < prefix; index += 1) {
    const row = seed[index]
    const startDate = starts.get(row.id)
    if (!startDate) return null
    semesters.push({ id: row.id, startDate })
    starts.delete(row.id)
  }

  let cursor = semesters[semesters.length - 1]?.id
  if (!cursor || prefix < seed.length) return cursor ? { semesters } : null
  const extras = [...starts.keys()].sort((a, b) => a.localeCompare(b))
  for (const id of extras) {
    let expected: string
    try {
      expected = nextSemesterId(cursor)
    } catch {
      break
    }
    if (id !== expected) break
    const startDate = starts.get(id)
    if (!startDate) break
    semesters.push({ id, startDate })
    cursor = id
  }

  return { semesters }
}

// Settings locks a start that has already arrived. Callers pass the app clock
// (`getNow`), which follows VITE_MOCK_NOW, so the emulator does not freeze
// semesters the rest of the app still treats as future.
export const isLockedSemesterStart = (
  startDate: string,
  today: string
): boolean => {
  const start = dayjs(startDate, SEMESTER_CALENDAR_DATE_FORMAT, true)
  return start.isValid() && startDate <= today
}

export const appTodayStamp = (): string =>
  getNow().format(SEMESTER_CALENDAR_DATE_FORMAT)

export const startsFor = (calendar: SemesterCalendar): string[] =>
  calendar.semesters.map((semester) => semester.startDate)

export const semesterForDate = (
  calendar: SemesterCalendar,
  date: Dayjs
): string => {
  let result = calendar.semesters[0]?.id ?? ""
  for (const semester of calendar.semesters) {
    const start = dayjs(semester.startDate, SEMESTER_CALENDAR_DATE_FORMAT, true)
    if (date.isBefore(start)) break
    result = semester.id
  }
  return result
}

export const indexOfSemester = (
  calendar: SemesterCalendar,
  semester: string
): number => calendar.semesters.findIndex((row) => row.id === semester)

export const semesterAt = (
  calendar: SemesterCalendar,
  index: number
): string | undefined => calendar.semesters[index]?.id

export const dayBeforeStart = (startDate: string): string =>
  dayjs(startDate, SEMESTER_CALENDAR_DATE_FORMAT, true)
    .subtract(1, "day")
    .format(SEMESTER_CALENDAR_DATE_FORMAT)

// The id of the first semester that has no real start date.
export const validateStarts = (semesters: SemesterStart[]): string | null => {
  for (const semester of semesters) {
    const start = dayjs(semester.startDate, SEMESTER_CALENDAR_DATE_FORMAT, true)
    if (!semester.startDate || !start.isValid()) return semester.id
  }
  return null
}

// Added rows start with an empty date. Ones left blank after the last dated
// semester are drafts, so a save leaves them out. A blank between dated
// semesters is a gap and stays in the list.
export const dropTrailingBlankSemesters = (
  semesters: SemesterStart[]
): SemesterStart[] => {
  let end = semesters.length
  while (end > 0 && !semesters[end - 1].startDate) end -= 1
  return end === semesters.length ? semesters : semesters.slice(0, end)
}

export type SemesterSaveProblem = {
  id: string
  kind: "missing" | "order"
}

// A save is refused when the dated semesters skip a term, or a later term
// starts before the one before it. Blank rows after the last dated semester
// are ignored.
export const semesterSaveError = (
  semesters: SemesterStart[]
): SemesterSaveProblem | null => {
  const kept = dropTrailingBlankSemesters(semesters)
  const missingDate = validateStarts(kept)
  if (missingDate) return { id: missingDate, kind: "missing" }
  for (let index = 1; index < kept.length; index += 1) {
    let expected: string
    try {
      expected = nextSemesterId(kept[index - 1].id)
    } catch {
      return { id: kept[index - 1].id, kind: "missing" }
    }
    if (kept[index].id !== expected) return { id: expected, kind: "missing" }
    const previous = dayjs(
      kept[index - 1].startDate,
      SEMESTER_CALENDAR_DATE_FORMAT,
      true
    )
    const start = dayjs(
      kept[index].startDate,
      SEMESTER_CALENDAR_DATE_FORMAT,
      true
    )
    if (start.isBefore(previous)) return { id: kept[index].id, kind: "order" }
  }
  return null
}
